/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/table.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import { Link, useLoaderData, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { PiPlusBold as Plus } from "react-icons/pi";
import { Alert, Button } from "rizzui";
import { EmptyState } from "@steelyard/theme";
import { PagedTable } from "../../../components/table/data-table";
import { ListHeader } from "../../../components/table/list-header";
import { activeStatusOptions, TableSearch } from "../../../components/table/toolbar";
import { brandColumns } from "./columns";
import { CATALOG_MAX_PAGE_SIZE, deactivateBrand, listBrands, reactivateBrand, loadActiveView, type Brand } from "../../../lib/services/catalog";
import { can } from "../../../lib/permissions";
import { activeViewParams, hrefWith, readActiveView, readPaging } from "../../../lib/paging";
import type { PermissionState, AccountSummary } from "../../../types";

/**
 * Ported from apps/admin/app/(app)/catalogue/brands/page.tsx. The pattern every other list page
 * in this migration copies: loader reads page and the All / Active / Inactive view from the URL (so
 * paging and filtering stay bookmarkable and survive a reload), PagedTable owns the frame,
 * deactivated rows stay visible and badged rather than hidden (data-model §6 — DELETE deactivates,
 * never removes).
 *
 * `GET /brands` filters on `activeOnly` and nothing else; `loadActiveView` reads the view on screen
 * and every count the status dropdown shows (see its own note for how Inactive is read).
 */
export async function brandsListLoader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const view = readActiveView(url.searchParams);
  const loaded = await loadActiveView(view, ({ page, pageSize, activeOnly }) => listBrands(page, activeOnly, pageSize), readPaging(url.searchParams));
  return { ...loaded, view };
}

export default function BrandsList() {
  const { result, view, complete, counts } = useLoaderData() as Awaited<ReturnType<typeof brandsListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const canWrite = can(permissionState, "catalogue.administer");

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Brands"
        count={result.totalCount}
        description="Every brand the catalogue can attach to a product."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Catalogue" }, { name: "Brands" }]}
      />

      {!complete ? (
        <Alert color="warning" variant="flat">
          Inactive shows the inactive brands among the first {CATALOG_MAX_PAGE_SIZE} of {counts.all}. The catalogue
          service has no inactive-only filter yet, so any beyond that are not listed here.
        </Alert>
      ) : null}

      <PagedTable<Brand>
        actions={
          canWrite ? (
            <Link to="/catalogue/brands/new">
              <Button as="span">
                <Plus aria-hidden className="me-1.5 size-4" />
                New brand
              </Button>
            </Link>
          ) : null
        }
        result={result}
        caption="Brands, with code and status"
        columns={brandColumns(canWrite ? (brand, active) => (active ? reactivateBrand : deactivateBrand)(brand.id) : undefined)}
        getRowId={(row) => row.id}
        status={{
          label: "Status",
          options: activeStatusOptions(view, (next) => hrefWith("/catalogue/brands", searchParams, activeViewParams(next)), counts),
        }}
        // The brand route takes no search parameter, so this filters the rows already loaded.
        search={(table) => <TableSearch table={table} placeholder={view === "inactive" ? "Search inactive brands…" : "Search this page…"} />}
        clear={{ active: view !== "active", href: hrefWith("/catalogue/brands", searchParams, activeViewParams("active")) }}
        empty={
          <EmptyState
            title={view === "active" ? "No active brands" : view === "inactive" ? "No inactive brands" : "No brands yet"}
            action={
              canWrite && view === "all" ? (
                <Link to="/catalogue/brands/new">
                  <Button as="span" size="sm">
                    <Plus aria-hidden />
                    New brand
                  </Button>
                </Link>
              ) : view !== "all" ? (
                <Link to="/catalogue/brands?all=1">
                  <Button as="span" variant="outline" size="sm">
                    Show every brand
                  </Button>
                </Link>
              ) : null
            }
          >
            {view === "active"
              ? "No brand is active right now — every one has been deactivated."
              : view === "inactive"
                ? "Every brand is active. A deactivated one stays listed here, never removed."
                : "Create the first one and it becomes selectable on every product."}
          </EmptyState>
        }
      />
    </div>
  );
}
